import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "@me8eon/translation";
import { EonUiForm } from "@eon-ui/eon-ui-components-react";
import { SimpleOutsideClick } from "@me8eon/outside_click";
import { useLanguage } from "@me8eon/language";
import "./search.dropdown.scss";
import { ListItem } from "./ListItem";
import { useSearchTextFilter } from "@me8eon/all_filters";
import { useFeatureFlag } from "@me8eon/featureflags";
import { immediateSearch, useTriggerSearch } from "@me8eon/search";
import { useDebouncedCallback } from "@me8eon/debounce";
import { useDataPlugins } from "@me8eon/data";
import { SearchTextFilter, searchTextName } from "@me8eon/searchtext_filters";

export type SearchDropdownProps = {
    dataToDisplay: any
    minChars?: number
    isDisabled?: boolean
    limitedHeight?: boolean
    listReverse?: boolean
    onChange?: (searchTerm: string) => void
    onSubmit?: (searchTerm: string) => void
    maxHeight?: string
}
export type SearchDropdown = (props: SearchDropdownProps) => React.ReactNode
import { useGuiFilters } from "@me8eon/filters";
import { useNavigate } from "react-router";

export const SearchDropdown: SearchDropdown = ({ minChars = 3, maxHeight, listReverse, limitedHeight, isDisabled, onSubmit, dataToDisplay }) => {

    const [searchTerm, setSearchText] = useSearchTextFilter();
    const [inputValue, setInputValue] = useState(searchTerm);
    const dataPlugins = useDataPlugins();
    const inputRef = useRef<HTMLInputElement>(null);
    const [guiFilters] = useGuiFilters();
    const [isOpen, setIsOpen] = useState(false);
    const [_language] = useLanguage();
    const t = useTranslation();
    const triggerSearch = useTriggerSearch<SearchTextFilter>();
    const navigate = useNavigate();

    useEffect(() => {
        setInputValue(searchTerm);
    }, [searchTerm]);

    const debouncedTriggerSearch = useDebouncedCallback(triggerSearch, 300);

    const formSubmit = async (clickedTerm: string) => {
        setSearchText(clickedTerm);
        inputRef.current?.blur();
        setIsOpen(false);
        onSubmit && onSubmit(clickedTerm);
        navigate(`${location.pathname}?q=${clickedTerm.replace(/&/g, "%26")}`);
    }

    return (
        <div style={{ width: "100%" }} className="search_dropdown__component">
            <SimpleOutsideClick onClickOutside={() => setIsOpen(false)}>
                <EonUiForm
                    onFormSubmit={() => formSubmit(inputValue)}
                    className={`search_dropdown ${isOpen ? "input--focused" : ""} 
                    ${inputValue.length > 0 && dataToDisplay && dataToDisplay.length > 0 && isOpen ? "list--exist" : ""}`}>
                    <input
                        type="text"
                        value={inputValue}
                        autoComplete="off"
                        onFocus={() => setIsOpen(true)}
                        ref={inputRef}
                        placeholder={t("searchPage.searchPlaceholder")}
                        title={t("searchPage.searchApps")}
                        disabled={isDisabled}
                        onChange={(e) => {
                            const st = e.target.value;
                            setInputValue(st);
                            debouncedTriggerSearch(immediateSearch, { ...guiFilters, [searchTextName]: st });
                        }}
                    />
                </EonUiForm>
                {isOpen && minChars && dataToDisplay && dataToDisplay.length > 0 && inputValue.length > 0 && (
                    <ul className={`result_ul
                        result_ul${limitedHeight ? "limited-height" : ""}
                        ${listReverse
                            ? "result_ul--reverse"
                            : "result_ul--normal"
                        }
                    `}
                        style={{
                            maxHeight: maxHeight ? maxHeight : "inherit",
                            overflowY: limitedHeight ? "hidden" : "auto",
                        }}
                    >
                        {dataToDisplay.map((item: any, index: number) => {
                            const type = item.type;
                            if (type) {
                                const plugin = dataPlugins[type];
                                if (!plugin) throw new Error(`Plugin not found for type: ${type}`);
                                const { DisplayDataOneLine } = plugin;
                                return <li key={index} className="result-list">
                                    <DisplayDataOneLine
                                        data={item}
                                        rootId={'some root id'}
                                        id={`${index}`}
                                        listItemClicked={
                                            (clickedItem) => {
                                                console.log("List item clicked", clickedItem);
                                                setInputValue(clickedItem);
                                                setIsOpen(false);
                                                formSubmit(clickedItem)
                                            }
                                        }
                                    />
                                </li>
                            }
                            return (
                                <ListItem
                                    key={index}
                                    label={item[`suggestion_${_language}`]}
                                    listItemClicked={
                                        (clickedItem) => {
                                            setInputValue(clickedItem);
                                            setIsOpen(false);
                                            formSubmit(clickedItem)
                                        }
                                    }
                                />
                            )
                        }
                        )}
                    </ul>
                )}
            </SimpleOutsideClick>
        </div >
    );
};
